import React from 'react';
import {
  TimelineWrapper,
  useTimeline,
  TimelineBox,
  TimelineTime,
  TimelineDividers,
  TimelineDivider,
} from 'planby';

// Custom replacements for missing PRO components
const TimelineWeekMonthBox = ({ children, left, width, timelineHeight, onClick, ...props }) => (
  <div
    onClick={onClick}
    style={{
      position: 'absolute',
      left: `${left}px`,
      width: `${width}px`,
      height: `${timelineHeight || 60}px`,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRight: '1px solid #718096',
      cursor: 'pointer',
    }}
    {...props}
  >
    {children}
  </div>
);

const TimelineWeekMonthDate = ({ children, ...props }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontSize: '12px',
      color: '#d1d1d1',
    }}
    {...props}
  >
    {children}
  </div>
);

const CurrentTime = () => null;

const getDaysArray = (start, end) => {
  const arr = [];
  const dt = new Date(start);
  const endDt = new Date(end);
  while (dt < endDt) {
    arr.push(new Date(dt));
    dt.setDate(dt.getDate() + 1);
  }
  return arr;
};

const getDayMonthName = (date) => {
  return date.toLocaleDateString('en-US', { weekday: 'short' }); // e.g., "Mon"
};

const formatWeekMonthDate = (date) => {
  return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit' }); // e.g., "06/15"
};

export function CustomTimeline(props) {
  const {
    isCurrentTime,
    handleTimelineClick,
    offsetStartHoursRange,
    numberOfHoursInDay,
    isBaseTimeFormat,
    hourWidth,
    sidebarWidth,
    isSidebar,
    isRTL,
    dayWidth,
    isToday,
    startDate,
    endDate,
    mode = { type: 'day', style: 'modern' },
  } = props;

  const isWeekMonthMode = mode.type === 'week' || mode.type === 'month';

  // Get hours array from free useTimeline
  const { time: hours, dividers, formatTime } = useTimeline(numberOfHoursInDay, isBaseTimeFormat);

  // Generate days array for week/month mode
  const days = React.useMemo(() => {
    if (isWeekMonthMode && startDate && endDate) {
      return getDaysArray(startDate, endDate);
    }
    return [];
  }, [isWeekMonthMode, startDate, endDate]);

  const timelineHeight = 60;

  const renderWeekMonth = (item, index) => {
    const totalDays = days.length || 7;
    const width = dayWidth / totalDays;
    const left = width * index;
    const position = {
      left,
      width,
    };
    const isModernStyle = mode.style === 'modern';

    return (
      <TimelineWeekMonthBox
        className="planby-timeline-box"
        data-testid="timeline-item"
        key={index}
        isToday={isToday}
        isWeekMonthMode={isWeekMonthMode}
        isCurrentTime={isCurrentTime}
        timelineHeight={timelineHeight}
        onClick={() => handleTimelineClick && handleTimelineClick(item.toISOString().split('T')[0], mode.type)}
        {...position}
      >
        <TimelineWeekMonthDate
          className="planby-timeline-week-month-date"
          isRTL={isRTL}
        >
          {isModernStyle && <span>{getDayMonthName(item)}</span>}
          <span>{formatWeekMonthDate(item)}</span>
        </TimelineWeekMonthDate>
      </TimelineWeekMonthBox>
    );
  };

  const renderDividers = (isNewDay) =>
    dividers.map((_, index) => (
      <TimelineDivider
        key={index}
        isNewDay={isNewDay}
        width={hourWidth / dividers.length}
      />
    ));

  const renderTime = (item, index) => {
    const isNewDay = (item + offsetStartHoursRange) % 24 === 0;
    const position = { left: hourWidth * index, width: hourWidth };
    
    return (
      <TimelineBox
        key={index}
        isToday={isToday}
        isCurrentTime={isCurrentTime}
        timelineHeight={timelineHeight}
        {...position}
        onClick={() => handleTimelineClick && handleTimelineClick(item + offsetStartHoursRange, mode.type)}
      >
        <TimelineTime isNewDay={isNewDay} isBaseTimeFormat={isBaseTimeFormat}>
          {formatTime(item + offsetStartHoursRange)}
        </TimelineTime>
        <TimelineDividers>{renderDividers(isNewDay)}</TimelineDividers>
      </TimelineBox>
    );
  };

  return (
    <TimelineWrapper
      className="planby-timeline-wrapper"
      data-testid="timeline"
      dayWidth={dayWidth}
      sidebarWidth={sidebarWidth}
      isSidebar={isSidebar}
    >
      {isCurrentTime && isToday && <CurrentTime />}
      {isWeekMonthMode
        ? days.map((item, index) => renderWeekMonth(item, index))
        : hours.map((item, index) => renderTime(item, index))}
    </TimelineWrapper>
  );
}

